import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { formatINR } from '@/lib/tracks';
import { PlanActivatedCelebration } from '@/components/PlanActivatedCelebration';
import { RazorpayPaymentButton } from '@/components/RazorpayPaymentButton';
import {
  CheckCircle2,
  ArrowLeft,
  ShieldCheck,
  Sparkles,
  Loader2,
} from 'lucide-react';

export default function CheckoutPage() {
  const nav = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [cooldown, setCooldown] = useState(60); // seconds until Activate is clickable

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const load = () =>
    api.get(`/orders/${orderId}`).then(({ data }) => {
      setOrder(data);
      setLoading(false);
    });

  useEffect(() => {
    load().catch(() => {
      toast.error('Order not found');
      nav('/pricing');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const activatePlan = async () => {
    setActivating(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/activate-instant`);
      const planId = data?.order?.plan_id;
      let expiresAt = null;
      if (planId && data?.plans) {
        const section = planId === 'ALL_ACCESS' ? 'ALL_ACCESS' : planId;
        expiresAt = data.plans[section]?.expires_at || null;
      }
      setCelebration({
        planId,
        planName: data?.order?.plan_name || order?.plan_name,
        amount: data?.order?.amount || order?.amount,
        orderShortId: (data?.order?.id || order?.id || '').slice(0, 8),
        expiresAt,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Activation failed. Please try again.');
    } finally {
      setActivating(false);
    }
  };

  if (loading || !order) {
    return (
      <AppShell>
        <div className="grid min-h-[50vh] place-items-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </AppShell>
    );
  }

  // Full-screen celebration after successful activation
  if (celebration) {
    const planId = celebration.planId || order?.plan_id;
    const goTakeMock = () => {
      if (planId === 'BOARDS_THEORY' || planId === 'BOARDS_PRACTICAL') nav('/boards');
      else if (planId === 'BOARDS_QBANK') nav('/qbank');
      else if (planId === 'JEE' || planId === 'NEET') nav(`/pick?track=${planId}`);
      else nav('/dashboard');
    };
    return (
      <PlanActivatedCelebration
        planName={celebration.planName}
        amount={celebration.amount}
        expiresAt={celebration.expiresAt}
        orderShortId={celebration.orderShortId}
        onPrimary={goTakeMock}
        onSecondary={() => nav('/dashboard')}
        onShareReferral={() => nav('/referral')}
      />
    );
  }

  const isPaid = order.status === 'paid';
  const buttonId = order.razorpay_button_id;

  return (
    <AppShell>
      <div className="space-y-6" data-testid="checkout-page">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav('/pricing')}
            className="gap-1.5"
            data-testid="back-to-plans"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to plans
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          {/* Payment card */}
          <Card>
            <CardContent className="space-y-6 p-6 sm:p-8">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Order</div>
                  <div className="font-display text-2xl font-semibold" data-testid="order-plan-name">
                    {order.plan_name}
                  </div>
                  <div className="text-sm text-muted-foreground">Order #{order.id.slice(0, 8)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Amount</div>
                  <div className="font-display text-3xl font-bold" data-testid="order-amount">
                    {formatINR(order.amount)}
                  </div>
                </div>
              </div>

              {isPaid ? (
                <div
                  className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4"
                  data-testid="payment-verified"
                >
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  <div>
                    <div className="font-semibold">Plan already active.</div>
                    <div className="text-sm text-muted-foreground">Head over to your dashboard.</div>
                  </div>
                  <Button
                    className="ml-auto"
                    onClick={() => nav('/dashboard')}
                    data-testid="go-to-dashboard"
                  >
                    Go to dashboard
                  </Button>
                </div>
              ) : (
                <>
                  {/* Step 1 — pay via Razorpay Payment Button */}
                  <div className="space-y-4 rounded-2xl border border-red-500/25 bg-red-500/5 p-5">
                    <div className="flex items-center gap-2">
                      <div className="grid h-8 w-8 place-items-center rounded-full bg-red-600 font-mono-ui text-sm font-bold text-white">
                        1
                      </div>
                      <div className="font-display text-lg font-semibold">
                        Pay {formatINR(order.amount)} securely via Razorpay
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Click the button below — the Razorpay checkout opens with the amount pre-filled.
                      Accepts UPI, cards, netbanking and wallets.
                    </div>
                    <div className="flex flex-col items-center gap-3 rounded-xl border bg-background/60 p-5">
                      {buttonId ? (
                        <RazorpayPaymentButton
                          buttonId={buttonId}
                          className="flex justify-center"
                        />
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          Payment gateway is warming up…
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Step 2 — activate */}
                  <div className="space-y-3 rounded-2xl border border-red-500/25 bg-red-500/10 p-5">
                    <div className="flex items-center gap-2">
                      <div className="grid h-8 w-8 place-items-center rounded-full bg-red-600 font-mono-ui text-sm font-bold text-white">
                        2
                      </div>
                      <div className="font-display text-lg font-semibold">
                        After the payment succeeds, activate your plan
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Once Razorpay confirms your payment, tap the button below to unlock your plan instantly.
                    </div>
                    <Button
                      onClick={activatePlan}
                      disabled={activating || cooldown > 0}
                      className="h-12 w-full bg-red-600 text-base font-semibold shadow-[0_0_28px_rgba(239,68,68,0.4)] hover:bg-red-500 disabled:opacity-70"
                      data-testid="activate-plan-button"
                    >
                      {activating ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Activating…
                        </>
                      ) : cooldown > 0 ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Please wait {cooldown}s while payment settles…
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-5 w-5" />
                          I've paid — Activate my plan
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <aside className="space-y-4">
            <Card>
              <CardContent className="space-y-2 p-5">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  <div className="font-display text-base font-semibold">Safe & instant</div>
                </div>
                <ul className="space-y-1.5 text-sm text-muted-foreground">
                  <li>• PCI-DSS-compliant payments powered by Razorpay.</li>
                  <li>• Your plan activates the moment you tap "Activate".</li>
                  <li>• 1-year access starts at activation.</li>
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="space-y-2 p-5">
                <div className="font-display text-base font-semibold">Trouble paying?</div>
                <p className="text-sm text-muted-foreground">
                  If the Razorpay window doesn't open, allow pop-ups for this site or open the checkout
                  page again in a fresh tab.
                </p>
                <Badge variant="secondary" data-testid="order-status">
                  Status: {order.status}
                </Badge>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </AppShell>
  );
}
